import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isAxiosError } from "axios";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";

export type FactureEncaissementContext = {
  id: number;
  numero: string;
  clientLabel: string;
  montantTTC: number;
  montantPaye: number;
  resteAPayer: number;
  devise: string;
  clientId: number;
};

const MODES = ["CASH", "MOBILE_MONEY", "BANQUE", "CARTE", "AUTRE"] as const;

type Props = {
  open: boolean;
  facture: FactureEncaissementContext | null;
  onClose: () => void;
  onSuccess: () => void;
};

export default function EncaissementFactureModal({
  open,
  facture,
  onClose,
  onSuccess,
}: Props): React.JSX.Element | null {
  const [montantPaye, setMontantPaye] = useState("");
  const [modePaiement, setModePaiement] = useState<string>("CASH");
  const [referencePaiement, setReferencePaiement] = useState("");
  const [datePaiement, setDatePaiement] = useState(() =>
    new Date().toISOString().slice(0, 16)
  );
  const [commentaire, setCommentaire] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const reste = facture?.resteAPayer ?? 0;

  const montantNum = useMemo(() => {
    const n = Number(String(montantPaye).replace(",", "."));
    return Number.isFinite(n) ? n : NaN;
  }, [montantPaye]);

  const refRecommandee = modePaiement === "MOBILE_MONEY" || modePaiement === "BANQUE" || modePaiement === "CARTE";
  const willSettleInvoice = Number.isFinite(montantNum) && montantNum >= reste - 0.0001;
  const receiptLabel = willSettleInvoice ? "quittance" : "reçu";
  const amountError = useMemo(() => {
    if (!montantPaye.trim()) return null;
    if (!Number.isFinite(montantNum) || montantNum <= 0) return "Saisissez un montant valide.";
    if (montantNum > reste + 0.0001) {
      return `Le montant saisi dépasse le solde de la dette (${reste.toLocaleString("fr-FR")} ${facture?.devise ?? ""}).`;
    }
    return null;
  }, [facture?.devise, montantNum, montantPaye, reste]);

  const reset = useCallback(() => {
    setMontantPaye("");
    setModePaiement("CASH");
    setReferencePaiement("");
    setDatePaiement(new Date().toISOString().slice(0, 16));
    setCommentaire("");
    setFile(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      handleClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, handleClose]);

  if (!open || !facture) return null;

  const validate = (): string | null => {
    if (!Number.isFinite(montantNum) || montantNum <= 0) return "Montant invalide.";
    if (montantNum > reste + 0.0001) return `Le montant ne peut pas dépasser le reste (${reste.toLocaleString("fr-FR")}).`;
    if (refRecommandee && !referencePaiement.trim()) {
      return "Référence recommandée pour ce mode de paiement.";
    }
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      toast.warning(err);
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("montantPaye", String(montantNum));
      fd.append("modePaiement", modePaiement);
      fd.append("datePaiement", new Date(datePaiement).toISOString());
      if (referencePaiement.trim()) fd.append("referencePaiement", referencePaiement.trim());
      if (commentaire.trim()) fd.append("commentaire", commentaire.trim());
      if (file) fd.append("preuvePaiement", file);

      const { data } = await api.post<{
        success?: boolean;
        paiementId?: number;
        quittance?: { id: number; numero: string } | null;
        message?: string;
      }>(`/finance/factures/${facture.id}/paiements`, fd);

      if (data?.success && data.paiementId) {
        toast.success("Encaissement enregistré.");
        if (data.quittance?.numero) {
          toast.info(`${willSettleInvoice ? "Quittance" : "Reçu d’acompte"} ${data.quittance.numero} créé.`);
        } else {
          toast.info("Document de paiement créé.");
        }
        onSuccess();
        handleClose();
      }
    } catch (e: unknown) {
      if (isAxiosError(e)) {
        const msg =
          (e.response?.data as { message?: string })?.message ||
          e.message ||
          "Erreur encaissement.";
        toast.error(msg);
      } else {
        toast.error("Erreur encaissement.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="encaissement-facture-title"
    >
      <div className="flex max-h-[90dvh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-slate-700 bg-slate-900 shadow-xl sm:max-h-[min(90dvh,900px)] sm:rounded-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-slate-800 bg-slate-900/95 px-4 py-4 backdrop-blur sm:px-5">
          <h2 id="encaissement-facture-title" className="text-lg font-semibold text-white">
            Encaisser une facture
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 text-sm text-slate-300 sm:px-5">
          <div className="rounded-xl border border-slate-700 bg-slate-950/80 p-4 text-xs leading-relaxed">
            <p>
              <span className="text-slate-500">Client</span>{" "}
              <span className="font-medium text-white">{facture.clientLabel}</span>
            </p>
            <p className="mt-1">
              <span className="text-slate-500">Facture</span>{" "}
              <span className="font-mono text-white">{facture.numero}</span>
            </p>
            <p className="mt-1">
              <span className="text-slate-500">Montant TTC</span>{" "}
              {facture.montantTTC.toLocaleString("fr-FR")} {facture.devise}
            </p>
            <p className="mt-1">
              <span className="text-slate-500">Déjà payé</span>{" "}
              {facture.montantPaye.toLocaleString("fr-FR")} {facture.devise}
            </p>
            <p className="mt-1 font-semibold text-amber-200">
              Reste à payer : {reste.toLocaleString("fr-FR")} {facture.devise}
            </p>
          </div>

          <p className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs font-medium text-emerald-100">
            Après validation, un document de paiement sera généré.
          </p>

          <label className="block">
            <span className="text-xs font-medium text-slate-400">Montant payé *</span>
            <input
              type="number"
              min={0}
              max={reste}
              step="0.01"
              value={montantPaye}
              onChange={(e) => setMontantPaye(e.target.value)}
              aria-invalid={amountError ? "true" : "false"}
              className={`mt-1 w-full rounded-xl border bg-slate-950 px-3 py-2 text-white ${
                amountError ? "border-rose-500" : "border-slate-700"
              }`}
            />
            {amountError ? (
              <p className="mt-1 text-xs font-medium text-rose-300">{amountError}</p>
            ) : null}
          </label>

          <label className="block">
            <span className="text-xs font-medium text-slate-400">Mode de paiement *</span>
            <select
              value={modePaiement}
              onChange={(e) => {
                setModePaiement(e.target.value);
                if (e.target.value === "CASH") setReferencePaiement("");
              }}
              className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
            >
              {MODES.map((m) => (
                <option key={m} value={m}>
                  {m.replace("_", " ")}
                </option>
              ))}
            </select>
          </label>

          {modePaiement !== "CASH" ? (
            <label className="block">
              <span className="text-xs font-medium text-slate-400">
                Référence {refRecommandee ? "(recommandée)" : ""}
              </span>
              <input
                value={referencePaiement}
                onChange={(e) => setReferencePaiement(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
                placeholder="Référence transaction / virement"
              />
            </label>
          ) : null}

          <label className="block">
            <span className="text-xs font-medium text-slate-400">Date du paiement *</span>
            <input
              type="datetime-local"
              value={datePaiement}
              onChange={(e) => setDatePaiement(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-slate-400">Preuve (optionnel)</span>
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="mt-1 w-full text-xs text-slate-400"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-slate-400">Commentaire</span>
            <textarea
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
            />
          </label>
        </div>

        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-slate-800 bg-slate-900/98 px-4 py-4 sm:px-5">
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex min-h-11 flex-1 items-center justify-center rounded-xl border border-slate-600 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 sm:flex-none sm:py-2"
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={submitting || amountError != null}
            onClick={() => void submit()}
            className="inline-flex min-h-11 flex-1 items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50 sm:flex-none sm:py-2"
          >
            {submitting
              ? "Envoi…"
              : `Valider et générer ${receiptLabel === "quittance" ? "la quittance" : "le reçu"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
